import * as THREE from "three";

// Room surface materials and their textures, installed on SceneManager.prototype.
export const roomMaterialMethods = {
  createMaterial(materialData, isCeiling = false, loadTextures = true, setTextureRepeat = true, surfaceAspect = 1, quarterTurn = false) {
    const material = new THREE.MeshStandardMaterial({
      side: THREE.DoubleSide,
      shadowSide: THREE.DoubleSide,
    });

    this._applyMaterialProperties(material, materialData);

    if (loadTextures) {
      const envManager = this.environmentManager;
      const isWallMaterial = Object.values(envManager.wallMaterials || {}).includes(materialData);
      this._loadMaterialTextures(material, materialData, isWallMaterial, setTextureRepeat, surfaceAspect, quarterTurn);
    }

    return material;
  },

  _applyMaterialProperties(material, materialData) {
    if (materialData?.properties) {
      material.roughness = materialData.properties.roughness || 0.8;
      material.metalness = materialData.properties.metalness || 0.0;

      if (materialData.properties.baseColor) {
        material.color.setHex(parseInt(materialData.properties.baseColor.replace("#", "0x")));
      }
    }
  },

  _loadMaterialTextures(material, materialData, isWall, setTextureRepeat = true, surfaceAspect = 1, quarterTurn = false) {
    // Same derivation the asset preload uses, so the two cannot drift apart.
    const texturePath = this.environmentManager.materialTextureDir(materialData, isWall);
    if (!texturePath) return;

    const repeat = setTextureRepeat ? materialData.properties?.repeat : null;
    const configure = (file) => this._loadAndConfigureTexture(texturePath + file, repeat, surfaceAspect, quarterTurn);

    const textures = materialData.textures;
    if (textures.diffuse) {
      const diffuseTex = configure(textures.diffuse);
      diffuseTex.colorSpace = THREE.SRGBColorSpace;
      material.map = diffuseTex;
    }
    if (textures.normal) {
      material.normalMap = configure(textures.normal);
    }
    if (textures.roughness) {
      material.roughnessMap = configure(textures.roughness);
    }
    if (textures.displacement && isWall) {
      material.displacementMap = configure(textures.displacement);
      material.displacementScale = materialData.properties?.displacementScale || 0.02;
    }
    if (textures.ao) {
      material.aoMap = configure(textures.ao);
      material.aoMapIntensity = materialData.properties?.aoIntensity || 1.0;
    }
    if (textures.arm) {
      const armTexture = configure(textures.arm);
      material.aoMap = armTexture;
      material.roughnessMap = armTexture;
      material.metalnessMap = armTexture;
      material.aoMapIntensity = materialData.properties?.aoIntensity || 1.0;
    }
  },

  /**
   * `repeat` may be a [u, v] pair (taken as authored) or a single number, meaning "this many
   * tiles across the image's own width axis" with the other axis derived from the image's
   * proportions once it loads, so a non-square texture tiles at square texels instead of
   * being stretched. surfaceAspect is the surface's extent along the image's width over its height.
   * `quarterTurn` lays the image's width along the surface's depth (v) instead of its width (u).
   */
  _loadAndConfigureTexture(path, repeat = null, surfaceAspect = 1, quarterTurn = false) {
    const derived = typeof repeat === 'number';
    const texture = this.textureLoader.load(
      path,
      derived ? (tex) => {
        this._deriveRepeat(tex, repeat, surfaceAspect);
        if (quarterTurn) this._quarterTurn(tex);
      } : undefined,
    );
    texture.wrapS = THREE.RepeatWrapping;
    texture.wrapT = THREE.RepeatWrapping;
    texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();

    if (repeat) {
      texture.repeat.set(...(derived ? [repeat, repeat] : repeat));
    }
    if (quarterTurn) this._quarterTurn(texture);

    return texture;
  },

  // (u, v) → (v, 1 − u) before the repeat, anchored on the same corner as the unturned image.
  _quarterTurn(texture) {
    texture.rotation = Math.PI / 2;
    texture.offset.set(0, texture.repeat.y);
  },

  _deriveRepeat(texture, tiles, surfaceAspect) {
    const { width, height } = texture.image || {};
    if (!width || !height) return;

    texture.repeat.set(tiles, (tiles * (width / height)) / surfaceAspect);
  },
};
